/* =========================================================
   GGS GOLD STREAK - geometry and dash measurement. 2026-09-08

   All the motion lives in ggs-goldstreak.css. This file does three
   things and nothing else:

   1. WRITES THE SHAPES in real pixels. The perimeter rect follows the
      footer's own box; the ring follows the Subscribe button, inflated
      4px so the gold runs on green rather than on the white pill; the
      underline follows the email field's bottom edge.

   2. SETS THE DASH from getTotalLength(). Every layer gets the same
      leading edge and a different tail - see the note in the CSS. It
      writes custom properties only, never `animation`, so a resize
      never restarts the loop mid-pass.

   3. GATES THE LOOP on visibility. The animations are paused in CSS
      and only run while the footer is on screen, so the sweep plays
      when someone arrives at the footer instead of having run
      unwatched since page load. That is what "comes onto screen"
      means here.

   FREEZE HOOK, for headless screenshots:
     #gs=4.2   pause every layer 4.2s into the 12s cycle
     #gs=off   remove the overlay entirely
   Query strings do not survive Shopify's preview redirect. Fragments
   do - the hero uses the same trick with #ggsf=.
   ========================================================= */
(function () {
  var wraps = document.querySelectorAll('[data-ggs-gs]');
  if (!wraps.length) return;

  var hash = (location.hash || '').match(/gs=([^&]+)/);
  var freeze = hash ? hash[1] : null;

  if (freeze === 'off') {
    Array.prototype.forEach.call(wraps, function (w) { w.remove(); });
    return;
  }

  function round(v) { return Math.round(v * 100) / 100; }

  /* The comet's core is 3% of whatever it is running around, held
     between 32px and 76px. Unclamped it would be a 120px bar on the
     footer and a 3px fleck on the pill; fixed, it would be the same
     length on both, which makes the pill pass look like a full ring
     lighting up rather than a light travelling.

     These numbers have moved twice. 20px/0.025/64 first; 26px when the
     ring read as a smudge; 32/0.03/76 on 2026-09-08 when the satin
     plate went in behind the footer and Aubrey asked for the streak to
     be stronger. A longer bright segment is most of what "stronger"
     means - width alone thickens the line without making it carry. */
  function dash(shapes, len) {
    var core = Math.max(32, Math.min(76, len * 0.03));
    var longest = core * 6;
    var gap = len + longest + 40;          /* wide enough that a second
                                              dash can never appear */
    Array.prototype.forEach.call(shapes, function (el) {
      var d = core * parseFloat(el.getAttribute('data-gs-tail') || '1');
      el.setAttribute('stroke-dasharray', round(d) + ' ' + round(gap));
      /* head position p: the dash covers [-offset, -offset + d], so a
         shared head needs offset = d - p. p runs 0 -> len + longest. */
      el.style.setProperty('--gs-o0', round(d));
      el.style.setProperty('--gs-o1', round(d - (len + longest)));
    });
  }

  function layoutFrame(wrap) {
    var host = wrap.parentNode;
    var svg = wrap.querySelector('svg');
    var w = host.clientWidth;
    var h = host.clientHeight;
    if (!w || !h) return false;

    var inset = parseFloat(
      getComputedStyle(host).getPropertyValue('--gs-inset')
    ) || 10;

    svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
    var rects = svg.querySelectorAll('rect');
    Array.prototype.forEach.call(rects, function (r) {
      r.setAttribute('x', inset);
      r.setAttribute('y', inset);
      r.setAttribute('width', Math.max(1, w - inset * 2));
      r.setAttribute('height', Math.max(1, h - inset * 2));
    });
    dash(rects, rects[0].getTotalLength());
    return true;
  }

  function layoutSign(wrap) {
    var row = wrap.parentNode;
    var svg = wrap.querySelector('svg');
    var btn = row.querySelector('button');
    var inp = row.querySelector('input[type="email"]');
    if (!btn || !inp) return false;

    var rb = row.getBoundingClientRect();
    if (!rb.width || !rb.height) return false;
    svg.setAttribute('viewBox', '0 0 ' + rb.width + ' ' + rb.height);

    /* the ring: the button's box, pushed 4px outward on every side */
    var bb = btn.getBoundingClientRect();
    var x = bb.left - rb.left - 4;
    var y = bb.top - rb.top - 4;
    var w = bb.width + 8;
    var h = bb.height + 8;
    var pills = svg.querySelectorAll('rect');
    Array.prototype.forEach.call(pills, function (r) {
      r.setAttribute('x', round(x));
      r.setAttribute('y', round(y));
      r.setAttribute('width', round(Math.max(1, w)));
      r.setAttribute('height', round(Math.max(1, h)));
      r.setAttribute('rx', round(h / 2));
    });
    dash(pills, pills[0].getTotalLength());

    /* the underline: along the field's own 1px bottom rule */
    var ib = inp.getBoundingClientRect();
    var ly = round(ib.bottom - rb.top - 0.5);
    var lines = svg.querySelectorAll('line');
    Array.prototype.forEach.call(lines, function (l) {
      l.setAttribute('x1', round(ib.left - rb.left));
      l.setAttribute('x2', round(ib.right - rb.left));
      l.setAttribute('y1', ly);
      l.setAttribute('y2', ly);
    });
    if (lines.length) dash(lines, lines[0].getTotalLength());
    return true;
  }

  function layout(wrap) {
    var ok = wrap.classList.contains('ggs-gs--sign')
      ? layoutSign(wrap)
      : layoutFrame(wrap);
    if (ok) wrap.classList.add('is-ready');
    return ok;
  }

  Array.prototype.forEach.call(wraps, function (wrap) {
    layout(wrap);

    if (freeze) {
      /* One frame of the cycle, held. Every layer shares --gs-loop and
         carries no delay of its own, so a single negative delay lands
         them all on the same instant. */
      var t = parseFloat(freeze) || 0;
      Array.prototype.forEach.call(
        wrap.querySelectorAll('.ggs-gs__t, .ggs-gs__sheen'),
        function (n) {
          n.style.animationDelay = '-' + t + 's';
          n.style.animationPlayState = 'paused';
        }
      );
      wrap.classList.add('is-live');
      return;
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          wrap.classList.toggle('is-live', e.isIntersecting);
        });
      }, { threshold: 0.2 }).observe(wrap.closest('.ggs-ftr') || wrap);
    } else {
      wrap.classList.add('is-live');
    }

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () { layout(wrap); });
      ro.observe(wrap.parentNode);
    } else {
      window.addEventListener('resize', function () { layout(wrap); });
    }
  });

  /* Hanken Grotesk arrives after first paint and changes the height of
     both the button and the field, so everything is measured again once
     the webfont has actually landed. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      Array.prototype.forEach.call(wraps, layout);
    });
  }
})();
